/**
 * Remember which certificates this viewer has already seen the reveal for, so a reveal
 * plays once and not on every return visit (VRG-790).
 *
 * ## The failure this exists to prevent
 *
 * A reveal is a flourish the FIRST time. On the twentieth it is an obstacle. The case
 * that decides the design is real and specific: a collector walking a city-wide
 * activation taps twenty tagged objects in an afternoon. Twenty unskippable-feeling
 * animations between them and twenty certificates is worse than no reveal at all — and
 * the person most affected is the brand's most engaged customer.
 *
 * So the rule is per CERTIFICATE, not per origin and not per product: tapping a second
 * object from the same drop is a new reveal (it is a different certificate), while
 * re-opening the one you already saw is not.
 *
 * ## Scope: per origin, which is per brand
 *
 * `localStorage` is partitioned by origin and an origin is one brand's domain, so this
 * can never carry a decision from one brand to another — the same property
 * `accessConsent.ts` relies on, and for the same reason it is safe whether Vera or the
 * brand is the data controller. Nothing here leaves the device and nothing reaches the
 * server: a reveal is not worth a write, and a server-side record would need an identity
 * this viewer does not have.
 *
 * ## It is a CONVENIENCE cache, so every path fails open to "play it"
 *
 * Private mode, disabled storage, a corrupt entry, a quota error — all of them end with
 * the reveal playing, which is the harmless answer. The opposite default would be worse:
 * a storage failure that read as "already seen" would silently disable the feature for
 * that viewer with nothing to indicate why.
 */

const KEY = 'vera:reveal-seen'

/**
 * How many certificates to remember. 200, with the oldest dropped first.
 *
 * Sized for the case above — a collector with a large collection, who is exactly the
 * person this must work for — while keeping the stored JSON small (200 UUIDs plus
 * timestamps is a few KB, well inside any browser's per-origin budget). Past the cap the
 * oldest entry is evicted, so the worst case is that someone who saw a reveal 201
 * certificates ago sees it once more.
 */
const MAX_ENTRIES = 200

/**
 * 365 days. Long, deliberately: the thing being remembered is "you have seen this
 * animation", and a reveal that reappears a year later is a reasonable thing to happen
 * on a certificate someone has not opened in a year. Short enough that the record does
 * not live forever.
 */
const TTL_MS = 365 * 24 * 60 * 60 * 1000

/** `{ [certificateId]: epochMs }` — the timestamp carries both TTL and LRU order. */
type SeenMap = Record<string, number>

const read = (): SeenMap => {
  try {
    const raw = window.localStorage.getItem(KEY)
    if (!raw) return {}

    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}

    const cutoff = Date.now() - TTL_MS
    const fresh: SeenMap = {}
    for (const [id, at] of Object.entries(parsed as Record<string, unknown>)) {
      // A non-numeric or expired value is dropped rather than trusted — which means the
      // reveal plays, the fail-open direction.
      if (typeof at === 'number' && Number.isFinite(at) && at > cutoff) fresh[id] = at
    }
    return fresh
  } catch {
    return {}
  }
}

/**
 * Has this viewer already seen this certificate's reveal?
 *
 * Returns `false` for an empty id, so a page that has not resolved its id yet is never
 * told "already seen" — that would skip the reveal on the exact cold-tap load it is for.
 */
export const hasSeenReveal = (certificateId: string | undefined | null): boolean => {
  if (!certificateId) return false
  return Boolean(read()[certificateId])
}

/**
 * Record that the reveal for this certificate has been seen.
 *
 * Called when the reveal REACHES ITS END or is skipped by the viewer — both count,
 * because in both cases the person has made their decision about this certificate's
 * reveal and should not be asked to make it again. It is deliberately NOT called when
 * the reveal fails (an error, a timeout, a browser that refused autoplay): a reveal the
 * viewer never actually saw has not been seen, and marking it would mean one unlucky
 * load permanently cost them the moment.
 */
export const markRevealSeen = (certificateId: string | undefined | null): void => {
  if (!certificateId) return
  try {
    const seen = read()
    seen[certificateId] = Date.now()

    const ids = Object.keys(seen)
    if (ids.length > MAX_ENTRIES) {
      // Oldest first, so the eviction is LRU by last view rather than arbitrary.
      ids
        .sort((a, b) => seen[a] - seen[b])
        .slice(0, ids.length - MAX_ENTRIES)
        .forEach(id => delete seen[id])
    }

    window.localStorage.setItem(KEY, JSON.stringify(seen))
  } catch {
    // Storage unavailable or over quota. The reveal still played; it will simply play
    // again next time, which is the harmless direction.
  }
}

/**
 * Does this viewer want motion at all?
 *
 * `prefers-reduced-motion: reduce` means no reveal — not a shortened one, not a static
 * poster held on screen. The setting is a request to be taken straight to the content,
 * and the content here is the certificate.
 *
 * Read in JS rather than expressed as a CSS media query because the decision is whether
 * to MOUNT the player at all. A CSS rule could only hide a video that was already
 * loading, which would still spend the bytes and still autoplay audio-less motion behind
 * an opacity of zero. `DrawerPopup` records the same reasoning for the same reason.
 */
export const prefersReducedMotion = (): boolean => {
  try {
    return (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    )
  } catch {
    // A browser without matchMedia gets the reveal. Failing open to "play" matches every
    // other path in this module.
    return false
  }
}
